import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Dumbbell, Zap, Target, Trophy, TrendingUp, Users, Star, CheckCircle } from 'lucide-react';
import ThemeToggle from '@/components/ThemeToggle';
import { API } from '@/App';
import axios from 'axios';

const Landing = ({ theme, onToggleTheme }) => {
  const navigate = useNavigate();
  const [usersCount, setUsersCount] = useState('');

  useEffect(() => {
    // Fetch real users count from database
    const fetchUsersCount = async () => {
      try {
        const response = await axios.get(`${API}/stats/users-count`);
        if (response.data.success) {
          const count = response.data.users_count;
          // Format the count
          if (count >= 1000) {
            setUsersCount(`${(count / 1000).toFixed(1)}K+`);
          } else {
            setUsersCount(count.toString());
          }
        }
      } catch (error) {
        console.error('Failed to fetch users count:', error);
        // Keep default value
      }
    };
    
    fetchUsersCount();
  }, []);

  const features = [
    {
      icon: Target,
      title: 'Personalized Workout Plans',
      description: 'Customized routines based on your goals, equipment, and fitness level.'
    },
    {
      icon: Zap,
      title: 'Gamified XP & Streak System',
      description: 'Earn XP, level up, and maintain streaks just like your favorite apps.'
    },
    {
      icon: TrendingUp,
      title: 'Progress Visualization',
      description: 'Track your journey with beautiful charts and achievement badges.'
    },
    {
      icon: Trophy,
      title: 'Smart Reminders & Rewards',
      description: 'Stay motivated with daily challenges and milestone celebrations.'
    }
  ];

  const stats = [
    { label: 'Workouts Tracked', value: '10K+', icon: Dumbbell },
    { label: 'Completion Rate', value: '80%', icon: CheckCircle },
    { label: 'Active Users', value: usersCount, icon: Users },
    { label: 'Average Rating', value: '4.9', icon: Star }
  ];

  const testimonials = [
    {
      name: 'Damar',
      avatar: 'https://i.imgur.com/xxUN2bG.jpeg',
      quote: 'As a college student with a packed schedule, I often struggle to find time for consistent workouts. Samastu has been incredibly helpful in making fitness more manageable. Its smart workout planner adjusts routines based on my weekly availability and fitness goals, making the planning process so much easier. Overall, Samastu’s thoughtful features help me stay organized, flexible, and motivated — even with a busy lifestyle.'
    },
    {
      name: 'Abiyasha',
      avatar: 'https://i.imgur.com/VeGSlKo.jpeg',
      quote: "As someone who's used to working out and wants to get back into it, I found Samastu super helpful! The standout feature for me is being able to customize the duration to fit my needs - it doesn't feel overwhelming. Plus, the gamified experience is on point. If they can nail general workouts like Strava does, Samastu might just be the next big thing 🔥🔥🔥"
    },
    {
      name: 'Fadlan',
      avatar: 'https://i.imgur.com/XrNcpqS.jpeg',
      quote: 'I truly hope Samastu continues to make an impact and inspire more people to live a healthy lifestyle. Let it be a motivation for those who want to start working out, a solution for those who lack the time, and a reminder for everyone to keep maintaining a healthy life.'
    }
  ];

  return (
    <div className="min-h-screen" style={{ backgroundColor: 'var(--bg-primary)' }}>
      {/* Navigation */}
      <nav className="fixed top-0 w-full backdrop-blur-md z-50 border-b" style={{ 
        backgroundColor: 'var(--card-bg)',
        borderColor: 'var(--border-color)'
      }}>
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Dumbbell className="w-8 h-8 text-[#D4AF37]" />
            <span className="text-2xl font-bold" style={{ color: 'var(--text-primary)' }}>Samastu</span>
          </div>
          <div className="flex items-center gap-4">
            <ThemeToggle theme={theme} onToggleTheme={onToggleTheme} />
            <Button
              onClick={() => navigate('/login')}
              data-testid="nav-login-button"
              className="border-2"
              style={{ 
                backgroundColor: 'var(--card-bg)',
                color: 'var(--text-primary)',
                borderColor: 'var(--border-color)'
              }}
            >
              Log In
            </Button>
            <Button
              onClick={() => navigate('/register')}
              data-testid="nav-signup-button"
              className="btn-dynamic font-semibold"
            >
              Get Started
            </Button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="pt-32 pb-20 px-6 bg-gray-100 lg:pt-60 lg:pb-48" style={{ backgroundColor: 'var(--bg-secondary)' }}>
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div className="animate-fadeIn">
              <h1 className="text-5xl lg:text-7xl font-bold mb-6 leading-tight" style={{ color: 'var(--text-primary)' }}>
                Make Workouts
                <span className="block gradient-text">Fun Again.</span>
              </h1>
              <p className="text-xl mb-8 leading-relaxed" style={{ color: 'var(--text-secondary)' }}>
                Samastu helps you build muscle, stay consistent, and enjoy the journey with gamified motivation.
              </p>
              <div className="flex flex-wrap gap-4">
                <Button
                  onClick={() => navigate('/register')}
                  data-testid="hero-get-started-button"
                  className="btn-dynamic font-semibold h-14 px-8 text-lg rounded-xl"
                >
                  Get Started Free
                </Button>
                <Button
                  onClick={() => navigate('/register')}
                  data-testid="hero-view-demo-button"
                  className="h-14 px-8 text-lg rounded-xl border-2"
                  style={{ 
                    backgroundColor: 'var(--card-bg)',
                    color: 'var(--text-primary)',
                    borderColor: 'var(--border-color)'
                  }}
                >
                  View Demo
                </Button>
              </div>
            </div>
            
            <div className="relative animate-slideUp">
              <div 
                className="rounded-2xl p-6 premium-shadow" 
                style={{ 
                  backgroundColor: 'var(--card-bg)', 
                  boxShadow: 'var(--shadow-xl)'}}>
                  <div className="flex items-center gap-3 mb-6">
                    <div className="w-12 h-12 bg-[#D4AF37] rounded-full flex items-center justify-center">
                      <Dumbbell className="w-6 h-6 text-white" />
                    </div>
                    <div>
                      <p className="text-sm" style={{ color: 'var(--text-secondary)' }}>Today's Workout</p>
                      <p className="font-bold" style={{ color: 'var(--text-primary)' }}>Full Body Starter</p>
                    </div>
                  </div>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between p-3 rounded-xl" style={{ backgroundColor: 'var(--bg-tertiary)' }}>
                      <span className="text-sm" style={{ color: 'var(--text-primary)' }}>Push-ups</span>
                      <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>3 × 10</span>
                    </div>
                    <div className="flex items-center justify-between p-3 rounded-xl" style={{ backgroundColor: 'var(--bg-tertiary)' }}>
                      <span className="text-sm" style={{ color: 'var(--text-primary)' }}>Squats</span>
                      <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>3 × 15</span>
                    </div>
                    <div className="flex items-center justify-between p-3 rounded-xl" style={{ backgroundColor: 'var(--bg-tertiary)' }}>
                      <span className="text-sm" style={{ color: 'var(--text-primary)' }}>Plank</span>
                      <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>2 × 30s</span>
                    </div>
                  </div>
                  <Button               
                  onClick={() => navigate('/login')}
                  data-testid="nav-login-button" 
                  className="w-full mt-6 btn-dynamic font-semibold">
                    Start Workout
                  </Button>
                </div>
              </div>
            </div>
          </div>
      </section>

      {/* Features Section */}
      <section className="py-20 px-6" style={{ backgroundColor: 'var(--bg-secondary)' }}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold mb-4" style={{ color: 'var(--text-primary)' }}>Everything You Need</h2>
            <p className="text-xl" style={{ color: 'var(--text-secondary)' }}>Powerful features designed to keep you motivated</p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, idx) => {
              const Icon = feature.icon;
              return (
                <div
                  key={idx}
                  className="rounded-2xl p-8 card-hover premium-shadow"
                  style={{ backgroundColor: 'var(--card-bg)', boxShadow: 'var(--shadow-lg)' }}
                  data-style={{ animationDelay: `${idx * 0.1}s` }}
                >
                  <div className="w-14 h-14 bg-[#D4AF37]/10 rounded-xl flex items-center justify-center mb-6">
                    <Icon className="w-7 h-7 text-[#D4AF37]" />
                  </div>
                  <h3 className="text-xl font-bold mb-3" style={{ color: 'var(--text-primary)' }}>{feature.title}</h3>
                  <p className="leading-relaxed" style={{ color: 'var(--text-secondary)' }}>{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-20 px-6" style={{ backgroundColor: 'var(--bg-secondary)' }}>
        <div className="max-w-7xl mx-auto">
          <div className="bg-gradient-to-br from-[#060736] to-[#c19b2e] rounded-3xl p-12 premium-shadow">
            <h2 className="text-4xl font-bold text-white text-center mb-12">Trusted by Hundreds</h2>
            <div className="grid md:grid-cols-4 gap-8">
              {stats.map((stat, idx) => {
                const Icon = stat.icon;
                return (
                  <div key={idx} className="text-center animate-fadeIn" style={{ animationDelay: `${idx * 0.1}s` }}>
                    <Icon className="w-10 h-10 text-white mx-auto mb-4" />
                    <div className="text-5xl font-bold text-white mb-2">{stat.value}</div>
                    <div className="text-white/90">{stat.label}</div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="py-20 px-6" style={{ backgroundColor: 'var(--bg-secondary)' }}>
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl lg:text-5xl font-bold mb-4 style={{ color: 'var(--text-secondary)' }}">What People Say</h2>
            <p className="text-xl style={{ color: 'var(--text-secondary)' }}">Join our growing community of fitness enthusiasts</p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, idx) => (
              <div
                key={idx}
                className="rounded-2xl p-8 premium-shadow animate-slideUp"
                style={{ animationDelay: `${idx * 0.1}s`, boxShadow: 'var(--shadow-lg)'}}
              >
                <div className="flex items-center gap-4 mb-6" >
                  <img src={testimonial.avatar} alt={testimonial.name} className="w-12 h-12 rounded-full" />
                  <div>
                    <p className="font-bold style={{ color: 'var(--text-secondary)'}}">{testimonial.name}</p>
                    <div className="flex gap-1">
                      {[...Array(5)].map((_, i) => (
                        <Star key={i} className="w-4 h-4 fill-[#D4AF37] text-[#D4AF37]" />
                      ))}
                    </div>
                  </div>
                </div>
                <p className="style={{ color: 'var(--text-secondary)'}} italic leading-relaxed text-justify">"{testimonial.quote}"</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-6" style={{ backgroundColor: 'var(--bg-secondary)' }}>
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl lg:text-5xl font-bold style={{ color: 'var(--text-secondary)' }} mb-6">
            Start Your Fitness Journey Today
          </h2>
          <p className="text-xl style={{ color: 'var(--text-secondary)' }} mb-8">
            Join hundreds of users making workouts fun and consistent with Samastu.
          </p>
          <Button
            onClick={() => navigate('/register')}
            data-testid="cta-get-started-button"
            className="cta-dynamic font-bold h-14 px-12 text-lg rounded-xl"
          >
            Get Started Free
          </Button>
          <p className="text-sm text-gray-500 mt-4">No credit card required • Free forever</p>
        </div>
      </section>

      {/* Footer */}
      <footer className="text-white py-12 px-6" style={{ backgroundColor: 'var(--bg-footer)' }} >
        <div className="max-w-7xl mx-auto text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Dumbbell className="w-8 h-8 text-[#D4AF37]" />
            <span className="text-2xl font-bold">Samastu</span>
          </div>
          <p className="text-gray-400 mb-4">Make workouts fun again.</p>
          <p className="text-sm text-gray-500">© 2025 Samastu. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
};

export default Landing;